import React, { useEffect, useRef, useState } from 'react';

export interface StorybookEmbedProps {
  storybookUrl?: string;
  selectedStory?: string;
  className?: string;
  onStoryChange?: (story: string) => void;
}

export const StorybookEmbed: React.FC<StorybookEmbedProps> = ({
  storybookUrl = 'http://localhost:6006',
  selectedStory,
  className = '',
  onStoryChange
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const handleLoad = () => {
      setIsLoading(false);
      setError(null);
    };

    const handleError = () => {
      setIsLoading(false);
      setError('Failed to load Storybook. Make sure it\'s running on ' + storybookUrl);
    };

    iframe.addEventListener('load', handleLoad);
    iframe.addEventListener('error', handleError);

    return () => {
      iframe.removeEventListener('load', handleLoad);
      iframe.removeEventListener('error', handleError);
    };
  }, [storybookUrl]);

  useEffect(() => {
    if (selectedStory && !isLoading && iframeRef.current) {
      try {
        // Navigate to specific story
        const url = new URL(storybookUrl);
        url.searchParams.set('path', `/story/${selectedStory}`);
        iframeRef.current.src = url.toString();
      } catch (err) {
        console.warn('Failed to navigate to story:', selectedStory);
      }
    }
  }, [selectedStory, storybookUrl, isLoading]);

  if (error) {
    return (
      <div className={`flex items-center justify-center h-full bg-gray-50 ${className}`}>
        <div className="text-center p-8">
          <div className="text-4xl mb-4">📚</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Storybook Connection Error
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            {error}
          </p>
          <div className="text-xs text-gray-500">
            <p>To start Storybook, run:</p>
            <code className="bg-gray-100 px-2 py-1 rounded mt-2 inline-block">
              npm run storybook
            </code>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative h-full ${className}`}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-sm text-gray-600">Loading Storybook...</p>
          </div>
        </div>
      )}
      
      <iframe
        ref={iframeRef}
        src={storybookUrl}
        className="w-full h-full border-0"
        title="Storybook"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
      />
      
      {/* Overlay for development mode indicator */}
      <div className="absolute top-2 right-2 z-20">
        <div className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">
          Live Preview
        </div>
      </div>
    </div>
  );
};